<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="red"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-start justify-between gap-4">
        <h2 class="flex-1 text-2xl font-bold">
          {{ $t("pages.quotes.add_quote") }}
        </h2>
        <default-button
          color="blue"
          :disabled="!validated"
          :loading="status.submitting"
          @click="submit"
        >
          {{ $t("global.inputs.labels.save") }}
        </default-button>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <text-area-input
          v-model="quote"
          :has-error="validationErrors.quote"
          :error-message="$t('global.inputs.errors.quote_mandatory')"
          :label="$t('global.inputs.labels.quote') + '*'"
          :placeholder="$t('global.inputs.placeholders.quote')"
        />
      </div>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("pages.quotes.who_said_it") }}
        </p>
        <p v-if="validationErrors.authors" class="mt-1 text-sm text-red-600">
          {{ $t("pages.quotes.one_author_minimum") }}
        </p>
        <multi-selector
          v-model="authors"
          label-key="name"
          index-key="uuid"
          :options="groupMembersSorted"
        />
      </div>

      <div class="mb-4 w-full">
        <text-input
          v-model="customAuthor"
          :label="$t('global.inputs.labels.quote_custom_author')"
          :placeholder="$t('global.inputs.placeholders.quote_custom_author')"
        />
      </div>

      <div class="mb-4 w-full">
        <text-area-input
          v-model="context"
          :has-error="validationErrors.description"
          error-message=""
          :label="$t('global.inputs.labels.quote_additional_context')"
          :placeholder="
            $t('global.inputs.placeholders.quote_additional_context')
          "
        />
      </div>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }} *
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>
        <div class="flex w-full flex-col items-center">
          <date-input v-model="timestamp" :date-time="true" />
        </div>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextInput from "@/components/Inputs/TextInput.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import MultiSelector from "@/components/Inputs/MultiSelector.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { generateTrigrams } from "@/helpers/utils/trigrams";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";

const { t } = useI18n();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const quotesStore = useQuotesStore();
const { maybePromptReview } = useAppReviewPrompt();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  submitting: false,
});
const context = ref<string | undefined>(undefined);
const quote = ref<string | undefined>(undefined);
const timestamp = ref<string | undefined>(undefined);
const customAuthor = ref<string | undefined>(undefined);
const authors = ref<string[]>([]);

// Computed properties
const groupData = computed(() => groupsStore.groupData);
const groupMembersSorted = computed(() => groupsStore.activeGroupMembersSorted);

const timestampValid = computed(() => !!timestamp.value);

const quoteValid = computed(() => {
  if (!quote.value) {
    return false;
  }
  let trimmedQuote = quote.value;
  trimmedQuote = trimmedQuote.replace(" ", "");
  return trimmedQuote && trimmedQuote.length > 0 && trimmedQuote.length < 350;
});

const contextValid = computed(() => {
  if (context.value) {
    return context.value.length < 350;
  }
  return true;
});

const authorsValid = computed(() => {
  if (authors.value.length === 0 && !customAuthor.value) {
    return false;
  } else if (authors.value.length === 0 && customAuthor.value) {
    return customAuthor.value.length > 1 && customAuthor.value.length < 350;
  }
  return true;
});

const validationErrors = computed(() => ({
  quote: !quoteValid.value,
  context: !contextValid.value,
  timestamp: !timestampValid.value,
  authors: !authorsValid.value,
  description: false,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function resetForm() {
  context.value = undefined;
  quote.value = undefined;
  customAuthor.value = undefined;
  authors.value = [];
  timestamp.value = dateObjectToDateString(new Date(), "yyyy-mm-ddThh:mm");
}

async function submit() {
  status.value.submitting = true;
  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    status.value.submitting = false;
    return;
  }

  try {
    await quotesStore.addQuote({
      quote: quote.value!,
      timestamp: new Date(timestamp.value!),
      authors: authors.value ? authors.value : [],
      custom_author: customAuthor.value ? customAuthor.value : "",
      context: context.value ? context.value : "",
      created_by: authStore.authUuid!,
      trigrams: generateTrigrams(quote.value!),
      reported_by: [],
      number_of_comments: 0,
    });

    setSuccessToast({
      emoji: "📢",
      title: t("global.toast.quote_saved"),
    });
    isOpen.value = false;
    maybePromptReview();
  } catch (e) {
    console.error("Error adding quote:", e);
  } finally {
    status.value.submitting = false;
  }
}

// Watch for opening to reset form
watch(isOpen, (newValue) => {
  if (newValue) {
    resetForm();
  }
});
</script>
